/**
 * The cells of the lists overview (spec-63 E5, design §4.7). Components only; the column map is in
 * `listColumns.tsx`.
 */
import { Check } from 'lucide-react'
import { Link } from 'react-router'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Dash } from '../list/commonCells'
import { todayIso } from '../dates'
import type { ListRow } from './types'
import { listRelative, listWhen } from './listDates'

/** The name as a link to the list page, with the start of the description under it. */
export function ListNameCell({ row }: { row: ListRow }) {
  return (
    <div className="flex min-w-0 flex-col">
      <Link to={`/relaties/lijsten/${row.id}`} className="truncate font-medium text-foreground underline-offset-2 hover:underline">
        {row.name}
      </Link>
      {row.description_text ? <span className="truncate text-xs text-muted-foreground">{row.description_text}</span> : null}
    </div>
  )
}

/** Weekday and date, or the period; the position to today is in the tooltip. */
export function ListDateCell({ row }: { row: ListRow }) {
  const when = listWhen(row.starts_on, row.ends_on)
  if (!when) return <Dash />
  return (
    <span className="whitespace-nowrap text-muted-foreground" title={listRelative(row.starts_on, row.ends_on, todayIso()) ?? undefined}>
      {when}
    </span>
  )
}

export function ListTypeCell({ row }: { row: ListRow }) {
  return <span className="text-muted-foreground">{row.type === 'dynamic' ? 'Dynamisch' : 'Statisch'}</span>
}

/** Static: up to three avatars and the number; dynamic: the live number alone (no avatars). */
export function ListMembersCell({ row }: { row: ListRow }) {
  if (row.members_count === null) return <Dash />
  const names = row.type === 'static' ? row.member_preview.slice(0, 3) : []
  return (
    <span className="inline-flex items-center gap-2">
      {names.length > 0 ? (
        <span className="flex -space-x-1.5" title={names.join(', ')}>
          {names.map((name) => (
            <EntityAvatar key={name} kind="person" name={name} size="xs" className="ring-2 ring-card" />
          ))}
        </span>
      ) : null}
      <span className="tabular-nums text-muted-foreground">{row.members_count}</span>
    </span>
  )
}

/** ✓ when the Mailchimp sync is on, else a dash. */
export function MailchimpCell({ row }: { row: ListRow }) {
  return row.mailchimp_sync_enabled ? (
    <span className="inline-flex items-center text-success" title="Mailchimp-sync staat aan">
      <Check className="size-4" aria-hidden />
      <span className="sr-only">Mailchimp-sync aan</span>
    </span>
  ) : (
    <Dash />
  )
}
